import AppContext from 'App/AppContext';
import { IPMessageBar, IPMessageBarButton, IPMessageBarType } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import AssessmentStartCard from 'components/AuditCriteria/AssessmentStartCard';
import AuditCriteriaCompareModal from 'components/AuditCriteria/AuditCriteriaCompareModal';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import { globalStackTokensGapSmall } from 'globalStyles';
import AuditCriteriaSet from 'models/auditCriteria';
import { AuditCriteriaLayer } from 'models/auditCriteriaGeneric';
import { getGenericAuditCriteriaSetName } from 'models/auditCriteriaGenericSets';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopyAuditCriteriaForEntity, apiRevertAuditCriteriaToGeneric } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateShort } from 'utils/datetime';

// A command of the layer header; its dialog is rendered by AuditCriteriaLayerDialogs, outside the host's stacks.
export type AuditCriteriaLayerCommand = 'copy' | 'revert' | 'compare' | 'assess';

const isGenericLayer = (set: AuditCriteriaSet): boolean =>
  set.layer === AuditCriteriaLayer.Family || set.layer === AuditCriteriaLayer.Subtype;

const isCopyLayer = (set: AuditCriteriaSet): boolean =>
  set.layer === AuditCriteriaLayer.Specific && set.sourceSetId !== undefined;

interface IAuditCriteriaLayerHeaderProps {
  criteriaSet: AuditCriteriaSet;
  // The item the criteria are of (its family names a generic set).
  entityType: number;
  // Without write rights the header names the layer only.
  readonly?: boolean;
  disabled?: boolean;
  onCommand: (command: AuditCriteriaLayerCommand) => void;
}

/**
 * Where an item's criteria come from (PLAN-075, spec 6.2), with its commands: a generic set ("Change for this item",
 * "Assess"), the item's own copy of a generic set ("Compare with the generic set", "Use the generic set from now on",
 * "Assess") or the item's own criteria ("Assess").
 */
const AuditCriteriaLayerHeader = (props: IAuditCriteriaLayerHeaderProps) => {
  const { t } = useTranslation(['criteria', 'translation', 'asset', 'processingActivity']);

  const set = props.criteriaSet;
  if (set.layer === AuditCriteriaLayer.None) return null;

  const isGeneric = isGenericLayer(set);
  const isCopy = isCopyLayer(set);

  const getText = (): string => {
    if (isGeneric) {
      const name = getGenericAuditCriteriaSetName(
        props.entityType,
        set.layer === AuditCriteriaLayer.Subtype ? set.subType : undefined,
        t,
      );

      return set.headVersion === undefined
        ? t('criteria:Generic.Layer.Generic', { name: name })
        : t('criteria:Generic.Layer.GenericVersion', { name: name, version: set.headVersion });
    }
    if (isCopy) {
      const copied = t('criteria:Generic.Layer.Copy', { date: toLocaleDateShort(set.copiedOn) });
      const sourceChanged =
        set.sourceChangedOn !== undefined &&
        set.copiedOn !== undefined &&
        set.sourceChangedOn.getTime() > set.copiedOn.getTime();

      return sourceChanged
        ? `${copied} ${t('criteria:Generic.Layer.SourceChanged', { date: toLocaleDateShort(set.sourceChangedOn) })}`
        : copied;
    }

    return t('criteria:Generic.Layer.Own');
  };

  const actions = props.readonly ? undefined : (
    <IPStack horizontal tokens={globalStackTokensGapSmall}>
      {isGeneric && (
        <IPMessageBarButton disabled={props.disabled} onClick={() => props.onCommand('copy')}>
          {t('criteria:Generic.Layer.CopyCommand')}
        </IPMessageBarButton>
      )}
      {isCopy && (
        <IPMessageBarButton disabled={props.disabled} onClick={() => props.onCommand('compare')}>
          {t('criteria:Generic.Layer.CompareCommand')}
        </IPMessageBarButton>
      )}
      {isCopy && (
        <IPMessageBarButton disabled={props.disabled} onClick={() => props.onCommand('revert')}>
          {t('criteria:Generic.Layer.RevertCommand')}
        </IPMessageBarButton>
      )}
      <IPMessageBarButton disabled={props.disabled} onClick={() => props.onCommand('assess')}>
        {t('criteria:Generic.Layer.AssessCommand')}
      </IPMessageBarButton>
    </IPStack>
  );

  return (
    <IPMessageBar messageBarType={IPMessageBarType.info} isMultiline={true} actions={actions}>
      {getText()}
    </IPMessageBar>
  );
};

interface IAuditCriteriaLayerDialogsProps {
  command: AuditCriteriaLayerCommand | undefined;
  entityType: number;
  entityId: number;
  entityName?: string;
  onBusyChange?: (isBusy: boolean) => void;
  // The copy or the revert changed the item's set.
  onChanged: (set: AuditCriteriaSet) => void;
  onClose: () => void;
}

// The dialogs of the layer header's commands, and the copy and revert they confirm.
export const AuditCriteriaLayerDialogs = (props: IAuditCriteriaLayerDialogsProps) => {
  const { t } = useTranslation(['criteria', 'translation']);
  const appContext = useContext(AppContext);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const run = async (command: 'copy' | 'revert') => {
    try {
      setIsBusy(true);
      props.onBusyChange?.(true);
      props.onClose();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result =
        command === 'copy'
          ? await apiCopyAuditCriteriaForEntity(props.entityType, props.entityId, accessToken)
          : await apiRevertAuditCriteriaToGeneric(props.entityType, props.entityId, accessToken);
      appContext.showNotification(
        command === 'copy' ? t('criteria:Generic.Layer.Copied') : t('criteria:Generic.Layer.Reverted'),
        false,
        false,
        true,
      );
      props.onChanged(result);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusy(false);
      props.onBusyChange?.(false);
    }
  };

  switch (props.command) {
    case 'copy':
    case 'revert': {
      const command = props.command;

      return (
        <DialogYesNo
          hidden={isBusy}
          title={
            command === 'copy'
              ? t('criteria:Generic.Layer.CopyConfirmTitle')
              : t('criteria:Generic.Layer.RevertConfirmTitle')
          }
          subText={
            command === 'copy' ? t('criteria:Generic.Layer.CopyConfirm') : t('criteria:Generic.Layer.RevertConfirm')
          }
          onYes={() => void run(command)}
          onNo={props.onClose}
        />
      );
    }
    case 'compare':
      return (
        <AuditCriteriaCompareModal
          entityType={props.entityType}
          entityId={props.entityId}
          entityName={props.entityName}
          onClose={props.onClose}
        />
      );
    case 'assess':
      return (
        <AssessmentStartCard
          entityType={props.entityType}
          entityIds={[props.entityId]}
          entityNames={props.entityName ? new Map([[props.entityId, props.entityName]]) : undefined}
          onClose={props.onClose}
        />
      );
    default:
      return null;
  }
};

export default AuditCriteriaLayerHeader;
